import React, { useState, useRef, useEffect } from 'react';
import { Minus, Plus } from 'lucide-react';

interface QuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  showInput?: boolean;
}

export const QuantityStepper: React.FC<QuantityStepperProps> = ({
  value,
  onChange,
  min = 1,
  max = 99999,
  step = 1,
  size = 'md',
  disabled = false,
  showInput = true
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const longPressInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDecrement = () => {
    if (value > min) {
      onChange(Math.max(min, value - step));
    }
  };

  const handleIncrement = () => {
    if (value < max) {
      onChange(Math.min(max, value + step));
    }
  };

  const handleLongPressStart = (action: 'increment' | 'decrement') => {
    longPressTimer.current = setTimeout(() => {
      longPressInterval.current = setInterval(() => {
        if (action === 'increment') {
          onChange(Math.min(max, value + step));
        } else {
          onChange(Math.max(min, value - step));
        }
      }, 100);
    }, 400);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (longPressInterval.current) {
      clearInterval(longPressInterval.current);
      longPressInterval.current = null;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const newValue = parseInt(inputValue);
    if (!isNaN(newValue) && newValue >= min && newValue <= max) {
      onChange(newValue);
    } else {
      setInputValue(value.toString());
    }
    setIsEditing(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    } else if (e.key === 'Escape') {
      setInputValue(value.toString());
      setIsEditing(false);
    }
  };

  const sizeStyles = {
    sm: {
      button: 'w-8 h-8',
      icon: 'h-3.5 w-3.5',
      display: 'w-12 h-8 text-sm',
      input: 'w-12 h-8 text-sm'
    },
    md: {
      button: 'w-11 h-11',
      icon: 'h-5 w-5',
      display: 'w-16 h-11 text-lg',
      input: 'w-16 h-11 text-lg'
    },
    lg: {
      button: 'w-14 h-14',
      icon: 'h-6 w-6',
      display: 'w-20 h-14 text-xl',
      input: 'w-20 h-14 text-xl'
    }
  };

  const styles = sizeStyles[size];

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleDecrement}
        onMouseDown={() => handleLongPressStart('decrement')}
        onMouseUp={handleLongPressEnd}
        onMouseLeave={handleLongPressEnd}
        onTouchStart={() => handleLongPressStart('decrement')}
        onTouchEnd={handleLongPressEnd}
        disabled={disabled || value <= min}
        className={`${styles.button} flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 touch-manipulation select-none`}
        aria-label="Decrease quantity"
      >
        <Minus className={`${styles.icon} text-gray-700 dark:text-gray-300`} />
      </button>

      {showInput ? (
        isEditing ? (
          <input
            ref={inputRef}
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            min={min}
            max={max}
            disabled={disabled}
            className={`${styles.input} text-center font-bold border-2 border-teal-500 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500/50`}
          />
        ) : (
          <button
            type="button"
            onClick={() => !disabled && setIsEditing(true)}
            disabled={disabled}
            className={`${styles.display} flex items-center justify-center font-bold text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-teal-300 dark:hover:border-teal-600 transition-colors cursor-text`}
          >
            {value}
          </button>
        )
      ) : (
        <div className={`${styles.display} flex items-center justify-center font-bold text-gray-900 dark:text-gray-100`}>
          {value}
        </div>
      )}

      <button
        type="button"
        onClick={handleIncrement}
        onMouseDown={() => handleLongPressStart('increment')}
        onMouseUp={handleLongPressEnd}
        onMouseLeave={handleLongPressEnd}
        onTouchStart={() => handleLongPressStart('increment')}
        onTouchEnd={handleLongPressEnd}
        disabled={disabled || value >= max}
        className={`${styles.button} flex items-center justify-center rounded-full bg-teal-100 dark:bg-teal-900/40 hover:bg-teal-200 dark:hover:bg-teal-800/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 touch-manipulation select-none`}
        aria-label="Increase quantity"
      >
        <Plus className={`${styles.icon} text-teal-700 dark:text-teal-300`} />
      </button>
    </div>
  );
};
